import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import { getListCategory, mergeGuessCart } from "../../services/purchaseApi";
import ErrorPopup from "../../components/common/ErrorPopup";
import BottomBarMobile from "../../components/common/BottomBarMobile";
import { LiaShippingFastSolid } from "react-icons/lia";
import { PiMapPin } from "react-icons/pi";
import { HiDotsVertical } from "react-icons/hi";
import { BsPencil } from "react-icons/bs";
import KonsultasiDokter from "../../assets/category-icons/icon-chat-doctor.png";
import All from "../../assets/category-icons/icon-wrapper.png";
import ProductCardNew from "./product/ProductCardNew";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  getDetailUserAndListOutlet,
  getDetailUserProfile,
  getListMyLocation,
} from "../../services/profileApi";
import ImageCarousel from "../../components/carousel/ImageCarousel";
import { listProduct } from "../../services/productApi";
import NotFound from "../../assets/notFound.png";
import { useNotification } from "../../components/context/NotificationContext";
import { checkDoctorAvailability } from "../../services/telemedicineApi";
import { getLabelConfig } from "../../config/labelConfig";
import LoadingBackdrop from "../common/LoadingBackdrop";
import {
  readSafeStorage,
  removeStorageKey,
  writeSafeStorage,
} from "../../utils/localStorageHelper";

const PurchaseList = () => {
  const machineId = localStorage.getItem("MachineId");
  const navigate = useNavigate();
  const { id } = useParams();
  const { isLogin, isMobileView, topicId } = useContext(GlobalStateContext);
  const containerRef = useRef(null);
  const { showNotification } = useNotification();
  const label = getLabelConfig();

  const [companyId, setCompanyId] = useState(readSafeStorage("companyId", ""));
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const [cardWidth, setCardWidth] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [listProducts, setListProducts] = useState([]);
  const [bestSellerProducts, setBestSellerProducts] = useState([]);
  const outletBanners = (() => {
    const stored = readSafeStorage("outlet_banners", "");
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [];
    } catch {
      return [];
    }
  })();
  const resolvedCompanyId = useMemo(() => {
    const parsedCompanyId = Number(companyId || 0);
    const parsedParamId = Number(id || 0);
    return parsedCompanyId > 0
      ? parsedCompanyId
      : parsedParamId > 0
        ? parsedParamId
        : 0;
  }, [companyId, id]);

  const reqBody = useMemo(
    () => ({
      company_id: resolvedCompanyId,
      limit: 20,
      offset: 1,
      keyword: "",
      category_id: 0,
      higher_price: 0,
      lower_price: 0,
      sort_by: 0,
    }),
    [resolvedCompanyId],
  );

  const bestSellerBody = useMemo(
    () => ({
      company_id: resolvedCompanyId,
      limit: 10,
      offset: 1,
      keyword: "",
      category_id: 0,
      higher_price: 0,
      lower_price: 0,
      sort_by: 5,
    }),
    [resolvedCompanyId],
  );

  const { data: dataList } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
    enabled: !!isLogin,
  });

  const outletAddress = readSafeStorage("outlet_address", "");
  const outletCity = readSafeStorage("outlet_city", "");

  const { data: dataUserAddress, isLoading: loadingUserAddress } = useQuery({
    queryKey: ["detail-list-outlet", resolvedCompanyId],
    queryFn: () => getDetailUserAndListOutlet(Number(resolvedCompanyId)),
    enabled: !!isLogin && resolvedCompanyId > 0,
  });

  const { data: dataListCategory, isFetching: fetchingCategory } = useQuery({
    queryKey: ["category-list"],
    queryFn: () => getListCategory(),
    refetchOnWindowFocus: false,
  });

  const { data: dataProfile } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const { mutate: mutateListProduct, isPending: isListLoading } = useMutation({
    mutationFn: listProduct,
    onSuccess: (data) => {
      if (data && data !== undefined) {
        setListProducts(data);
      }
    },
  });

  const { mutate: mutateBestSeller, isPending: isBestSellerLoading } =
    useMutation({
      mutationFn: listProduct,
      onSuccess: (data) => {
        setBestSellerProducts(data);
      },
    });

  const { mutate: mutateCheckDoctor, isPending: isLoadingCheckDoctor } =
    useMutation({
      mutationFn: checkDoctorAvailability,
      onSuccess: (data) => {
        if (!isLogin) {
          navigate("/profile");
        } else if (isLogin && dataList === null) {
          showNotification("Silahkan tambahkan alamat terlebih dahulu", "info");
          navigate("/location-address/my-address/new");
        } else if (dataProfile.nik === "0") {
          showNotification(
            "Silahkan tambahkan No. NIK terlebih dahulu",
            "info",
          );
          navigate("/profile/change");
        } else if (topicId && topicId.trim() !== "") {
          navigate(`/consultation/chat-with-doctor/${topicId}`, {
            state: { type: 2 },
          });
        } else {
          navigate("/consultation/detail-form", {
            state: {
              doctorData: data,
            },
          });
        }
      },
      onError: (err) => {
        if (err?.message === "Token expired") {
          showNotification("Silahkan login terlebih dahulu", "info");
          navigate("/profile");
        }
        showNotification(err?.message, "error");
      },
    });

  const { mutate: mutateMergeGuessCart } = useMutation({
    mutationFn: mergeGuessCart,
  });

  useEffect(() => {
    if (!resolvedCompanyId) return;
    mutateListProduct(reqBody);
    mutateBestSeller(bestSellerBody);
  }, [resolvedCompanyId, reqBody, bestSellerBody]);

  useEffect(() => {
    if (resolvedCompanyId && machineId && isLogin)
      mutateMergeGuessCart({
        uuid: machineId,
        company_id: Number(resolvedCompanyId),
      });
  }, [isLogin, machineId, resolvedCompanyId, mutateMergeGuessCart]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "companyId") {
        setCompanyId(e.newValue || "");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (!companyId && id && Number(id) > 0) {
      const normalized = String(id);
      setCompanyId(normalized);
      writeSafeStorage("companyId", normalized);
    }
  }, [companyId, id]);

  const dummyProducts = Array(5).fill({
    id: 0,
    name: "",
    media: [],
    price: 0,
    discount_type: 0,
    discount_price: null,
    discount: null,
    stock: 0,
    quantity: 0,
    get_product_id: 0,
  });

  const loadingProductCards = Array.from({ length: 10 }, (_, index) => ({
    ...dummyProducts[0],
    id: `loading-${index}`,
  }));

  const calculateCardWidth = () => {
    if (typeof window === "undefined") return 0;

    const screenWidth = window.innerWidth;
    const MAX_CARD_WIDTH = 243.2;

    let cols, gap, margin;

    if (screenWidth >= 1280) {
      cols = 5;
      gap = 16;
      margin = 32;
    } else if (screenWidth >= 1024) {
      cols = 4;
      gap = 16;
      margin = 32;
    } else if (screenWidth >= 768) {
      cols = 3;
      gap = 16;
      margin = 32;
    } else if (screenWidth >= 640) {
      cols = 2;
      gap = 16;
      margin = 16;
    } else {
      cols = 2;
      gap = 4;
      margin = 8;
    }

    const availableWidth = screenWidth - margin * 2;
    const totalGaps = (cols - 1) * gap;
    let width = (availableWidth - totalGaps) / cols;

    if (screenWidth >= 1280 && width > MAX_CARD_WIDTH) {
      width = MAX_CARD_WIDTH;
    }

    return Math.floor(width);
  };

  useEffect(() => {
    const updateCardWidth = () => {
      setCardWidth(calculateCardWidth());
    };

    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);

    return () => window.removeEventListener("resize", updateCardWidth);
  }, []);

  const updateScrollState = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setCanScroll({
        left: scrollLeft > 10,
        right: scrollLeft < scrollWidth - clientWidth - 10,
      });
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", updateScrollState);
      updateScrollState();
      return () => container.removeEventListener("scroll", updateScrollState);
    }
  }, [bestSellerProducts?.length, dummyProducts.length, cardWidth]);

  const scroll = (direction) => {
    if (containerRef.current && cardWidth > 0) {
      const screenWidth = window.innerWidth;
      const gap = screenWidth >= 640 ? 16 : 4;
      const scrollAmount = cardWidth + gap;

      containerRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const getGapClass = () => {
    if (typeof window === "undefined") return "gap-1";
    return window.innerWidth >= 640 ? "gap-4" : "gap-1";
  };

  const destinationLabel =
    isLogin && dataUserAddress && !loadingUserAddress
      ? dataUserAddress?.user_address?.address || "Belum ditentukan"
      : "Belum ditentukan";

  useEffect(() => {
    if (!isLogin) {
      removeStorageKey("address");
      return;
    }

    const addressValue = dataUserAddress?.user_address?.address;
    if (!writeSafeStorage("address", addressValue) && !loadingUserAddress) {
      removeStorageKey("address");
    }
  }, [isLogin, dataUserAddress, loadingUserAddress]);

  return (
    <>
      {isMobileView ? (
        <>
          <LoadingBackdrop isLoading={isLoadingCheckDoctor} />
          <div className="pt-2 px-[12px] max-w-7xl lg:px-8 xl:px-0 mx-auto mb-4 animate-page-enter">
            <div className="bg-gray-50 rounded-2xl p-3 mb-4 flex gap-3 items-center border border-gray-200">
              <div className="flex flex-col items-center">
                <LiaShippingFastSolid size={20} className="text-secondary" />
                <HiDotsVertical size={12} className="text-gray-500" />
                <PiMapPin size={20} className="text-error" />
              </div>
              <div className="flex flex-col flex-1 text-sm gap-2 min-w-0">
                <p className="text-gray-900 flex items-center gap-1 truncate">
                  Dikirim Dari:{" "}
                  <span className="text-gray-500 marquee-container">
                    <span
                      className={`marquee-content ${outletAddress && outletAddress.length < 30 ? "no-marquee" : ""}`}
                    >
                      {outletAddress ? `${outletAddress}, ${outletCity}` : "-"}
                    </span>
                  </span>
                </p>
                <hr />
                <p className="text-gray-900 flex items-center gap-1 truncate">
                  Tujuan:{" "}
                  <span className="text-gray-500 marquee-container">
                    <span
                      className={`marquee-content ${
                        destinationLabel.length < 30 ? "no-marquee" : ""
                      }`}
                    >
                      {destinationLabel}
                    </span>
                  </span>
                </p>
              </div>
              <button
                onClick={() => {
                  if (isLogin) {
                    navigate("/location-address");
                  } else {
                    navigate("/profile");
                  }
                }}
                className="rounded-lg border border-gray-200 p-2 cursor-pointer bg-white hover:bg-gray-100 flex-shrink-0"
              >
                <BsPencil size={20} className="text-gray-500" />
              </button>
            </div>

            {outletBanners && outletBanners.length > 0 && (
              <ImageCarousel images={outletBanners} />
            )}

            <div className="mt-6 mb-4">
              <h1 className="font-semibold text-lg mb-4 shine-text">
                Kategori
              </h1>
              <div className="grid grid-cols-4 gap-x-4">
                {fetchingCategory ? (
                  [1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                    <div
                      key={item}
                      className="flex flex-col items-center p-1 animate-pulse"
                    >
                      <div className="w-12 h-12 bg-slate-200 rounded-lg mb-1"></div>
                      <div className="h-4 rounded-md w-3/4 bg-slate-200"></div>
                    </div>
                  ))
                ) : (
                  <>
                    <div
                      onClick={() => {
                        if (isLogin && dataList === null) {
                          navigate("/location-address/my-address/new");
                        } else
                          mutateCheckDoctor({
                            internal_only:
                              label.brandName === "Orlin" ? true : false,
                          });
                      }}
                      className="flex flex-col items-center p-1"
                    >
                      <div className="w-12 h-12 z-[98] mb-1">
                        <img src={KonsultasiDokter} alt="Konsultasi Dokter" />
                      </div>
                      <p className="text-center leading-tight text-sm font-medium">
                        Konsultasi Dokter
                      </p>
                    </div>
                    {dataListCategory &&
                      dataListCategory.map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col items-center p-1"
                          onClick={() =>
                            navigate(`/purchase/category/${item.id}`)
                          }
                        >
                          <div className="w-12 h-12 z-[98] mb-1">
                            <img src={item.icon_url} alt="Susu" />
                          </div>
                          <p className="text-center leading-tight text-sm font-medium">
                            {item.name}
                          </p>
                        </div>
                      ))}
                    <div
                      className="flex flex-col items-center p-1"
                      onClick={() => navigate("/purchase/category-list")}
                    >
                      <div className="w-12 h-12 z-[98] mb-1">
                        <img src={All} alt="All" />
                      </div>
                      <p className="text-center leading-tight text-sm font-medium">
                        Lihat Semua
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 mb-4">
              <h1 className="font-semibold text-lg mb-1 shine-text">
                Produk Terlaris
              </h1>
            </div>
          </div>
          <div className="px-2 md:px-[10vw]">
            <div className="mt-4 lg:mt-12 relative">
              {!isBestSellerLoading && bestSellerProducts?.length > 0 && (
                <>
                  <button
                    onClick={() => scroll("left")}
                    disabled={!canScroll.left}
                    className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 lg:-translate-x-4 z-10 p-2 lg:p-3 rounded-full bg-white shadow-lg border border-gray-200 transition-all duration-300 ${
                      canScroll.left
                        ? "opacity-50 hover:bg-gray-50 hover:shadow-xl cursor-pointer"
                        : "opacity-0 cursor-not-allowed"
                    }`}
                  >
                    <ChevronLeft size={20} className="text-gray-700" />
                  </button>

                  <button
                    onClick={() => scroll("right")}
                    disabled={!canScroll.right}
                    className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 lg:translate-x-4 z-10 p-2 lg:p-3 rounded-full bg-white shadow-lg border border-gray-200 transition-all duration-300 ${
                      canScroll.right
                        ? "opacity-50 hover:bg-gray-50 hover:shadow-xl cursor-pointer"
                        : "opacity-0 cursor-not-allowed"
                    }`}
                  >
                    <ChevronRight size={20} className="text-gray-700" />
                  </button>
                </>
              )}

              <div
                ref={containerRef}
                className="overflow-x-auto scrollbar-hide scroll-smooth"
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <div className={`flex ${getGapClass()} pb-2`}>
                  {isBestSellerLoading ? (
                    dummyProducts.map((product, index) => (
                      <div
                        key={`dummy-${index}`}
                        className="shrink-0 product-card snap-start"
                        style={{
                          width: cardWidth > 0 ? `${cardWidth}px` : "auto",
                        }}
                      >
                        <ProductCardNew item={product} isLoading={true} />
                      </div>
                    ))
                  ) : !isBestSellerLoading &&
                    bestSellerProducts?.length === 0 ? (
                    <div className="py-2 w-full flex items-center justify-center">
                      <img src={NotFound} alt="Not Found" className="w-1/3" />
                    </div>
                  ) : (
                    bestSellerProducts?.map((product) => (
                      <div
                        key={product.product_id}
                        className="shrink-0 product-card snap-start"
                        style={{
                          width: cardWidth > 0 ? `${cardWidth}px` : "auto",
                        }}
                      >
                        <ProductCardNew
                          item={product}
                          isLoading={false}
                          mutate={() => mutateListProduct(reqBody)}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 px-2">
            <div className="mt-6 mb-4 px-2">
              <h1 className="font-semibold text-lg mb-1 shine-text">
                Produk Lainnya
              </h1>
            </div>
            <div
              className={`w-full grid ${listProducts.length === 0 && !isListLoading ? "grid-cols-1" : "grid-cols-2"} sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1`}
            >
              {isListLoading && listProducts.length === 0
                ? loadingProductCards.map((product, i) => (
                    <ProductCardNew key={i} item={product} isLoading={true} />
                  ))
                : listProducts && listProducts.length > 0
                  ? listProducts.map((product, i) => (
                      <ProductCardNew
                        key={product.id || i}
                        item={product}
                        isLoading={false}
                      />
                    ))
                  : null}
            </div>
          </div>
        </>
      ) : (
        <div className="min-h-screen animate-page-enter overflow-x-hidden">
          <LoadingBackdrop isLoading={isLoadingCheckDoctor} />
          <div className="overflow-hidden py-8 pb-20">
            <div className="max-w-7xl mx-auto">
              <div className="mb-7 flex items-center gap-4 rounded-2xl border-2 bg-[#6FAF4F] border-white/50 px-5 py-3">
                <div className="flex flex-col items-center gap-1">
                  <LiaShippingFastSolid size={22} className="text-white" />
                  <div className="w-px h-5 bg-white/30" />
                  <PiMapPin size={22} className="text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-[12px] text-white/60 whitespace-nowrap">
                      Dikirim dari
                    </span>
                    <span className="text-[13px] font-semibold text-white overflow-hidden text-ellipsis whitespace-nowrap">
                      {outletAddress
                        ? `${outletAddress}, ${outletCity}`
                        : "Memuat..."}
                    </span>
                  </div>
                  <div className="h-px bg-white/15 mb-1.5" />
                  <div className="flex items-center gap-4 mt-4">
                    <span className="text-[12px] text-white/60 whitespace-nowrap">
                      Tujuan
                    </span>
                    <span className="text-[13px] font-semibold text-white overflow-hidden text-ellipsis whitespace-nowrap">
                      {destinationLabel}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() =>
                    navigate(isLogin ? "/location-address" : "/profile")
                  }
                  className="flex items-center gap-1.5 bg-white/15 border border-white/25 rounded-[10px] py-2 px-3.5 text-white text-[13px] font-medium cursor-pointer shrink-0 transition-colors hover:bg-white/25"
                >
                  <BsPencil size={14} />
                  Ubah
                </button>
              </div>

              {outletBanners && outletBanners.length > 0 && (
                <div className="rounded-[32px] overflow-hidden">
                  <ImageCarousel images={outletBanners} />
                </div>
              )}
            </div>
          </div>

          <div className="max-w-7xl mx-auto -mt-10 relative z-10">
            <div className="bg-white rounded-2xl p-8 mb-16">
              <div className="flex items-center justify-between mb-5">
                <h2 className="m-0 text-2xl font-semibold shine-text">
                  Kategori
                </h2>
                <button
                  onClick={() => navigate("/purchase/category-list")}
                  className="text-[13px] font-medium text-[#1a6fa8] bg-transparent border-0 cursor-pointer py-1 px-0"
                >
                  Lihat semua →
                </button>
              </div>

              {fetchingCategory ? (
                <div style={{ display: "flex", gap: 12 }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          background: "#e2e8f0",
                        }}
                        className="animate-pulse"
                      />
                      <div
                        style={{
                          width: 48,
                          height: 10,
                          borderRadius: 6,
                          background: "#e2e8f0",
                        }}
                        className="animate-pulse"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => {
                      if (isLogin && dataList === null) {
                        navigate("/location-address/my-address/new");
                      } else
                        mutateCheckDoctor({
                          internal_only:
                            label.brandName === "Orlin" ? true : false,
                        });
                    }}
                    style={{
                      flex: "1 1 80px",
                      minWidth: 80,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "12px 8px",
                      borderRadius: 14,
                      border: "2px solid transparent",
                      background: "#f0f9ff",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#dbeafe";
                      e.currentTarget.style.borderColor = "#93c5fd";
                      e.currentTarget.style.transform = "translateY(-3px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f0f9ff";
                      e.currentTarget.style.borderColor = "transparent";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <img
                      src={KonsultasiDokter}
                      alt="Konsultasi Dokter"
                      style={{ width: 48, height: 48, objectFit: "contain" }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#1e40af",
                        textAlign: "center",
                        lineHeight: 1.3,
                      }}
                    >
                      Konsultasi Dokter
                    </span>
                  </button>

                  {dataListCategory &&
                    dataListCategory.slice(0, 7).map((item, i) => (
                      <button
                        key={i}
                        onClick={() =>
                          navigate(`/purchase/category/${item.id}`)
                        }
                        style={{
                          flex: "1 1 80px",
                          minWidth: 80,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "12px 8px",
                          borderRadius: 14,
                          border: "2px solid transparent",
                          background: "#f8fafc",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          gap: 8,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f0fdf4";
                          e.currentTarget.style.borderColor = "#86efac";
                          e.currentTarget.style.transform = "translateY(-3px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f8fafc";
                          e.currentTarget.style.borderColor = "transparent";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <img
                          src={item.icon_url}
                          alt={item.name}
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "contain",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#374151",
                            textAlign: "center",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.name}
                        </span>
                      </button>
                    ))}

                  <button
                    onClick={() => navigate("/purchase/category-list")}
                    style={{
                      flex: "1 1 80px",
                      minWidth: 80,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "12px 8px",
                      borderRadius: 14,
                      border: "2px solid transparent",
                      background: "#f8fafc",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fef9ee";
                      e.currentTarget.style.borderColor = "#fcd34d";
                      e.currentTarget.style.transform = "translateY(-3px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f8fafc";
                      e.currentTarget.style.borderColor = "transparent";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <img
                      src={All}
                      alt="Lihat Semua"
                      style={{ width: 48, height: 48, objectFit: "contain" }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#374151",
                        textAlign: "center",
                        lineHeight: 1.3,
                      }}
                    >
                      Lihat Semua
                    </span>
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 18,
                }}
              >
                <div>
                  <h2 className="text-2xl font-semibold shine-text">
                    Produk Terlaris
                  </h2>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 13,
                      color: "#64748b",
                    }}
                  >
                    Pilihan terbaik dari pelanggan kami
                  </p>
                </div>
              </div>

              <div style={{ position: "relative" }}>
                {bestSellerProducts?.length > 0 && (
                  <>
                    <button
                      onClick={() => scroll("left")}
                      disabled={!canScroll.left}
                      style={{
                        position: "absolute",
                        left: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: canScroll.left ? "#fff" : "transparent",
                        border: canScroll.left ? "1px solid #e2e8f0" : "none",
                        boxShadow: canScroll.left
                          ? "0 4px 16px rgba(0,0,0,0.12)"
                          : "none",
                        cursor: canScroll.left ? "pointer" : "not-allowed",
                        opacity: canScroll.left ? 1 : 0,
                        transition: "all 0.25s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ChevronLeft style={{ color: "#374151", fontSize: 22 }} />
                    </button>

                    <button
                      onClick={() => scroll("right")}
                      disabled={!canScroll.right}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: canScroll.right ? "#fff" : "transparent",
                        border: canScroll.right ? "1px solid #e2e8f0" : "none",
                        boxShadow: canScroll.right
                          ? "0 4px 16px rgba(0,0,0,0.12)"
                          : "none",
                        cursor: canScroll.right ? "pointer" : "not-allowed",
                        opacity: canScroll.right ? 1 : 0,
                        transition: "all 0.25s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ChevronRight
                        style={{ color: "#374151", fontSize: 22 }}
                      />
                    </button>
                  </>
                )}

                <div
                  ref={containerRef}
                  className="overflow-x-auto scrollbar-hide scroll-smooth"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  <div className="flex gap-4 pb-4">
                    {isBestSellerLoading
                      ? loadingProductCards.map((product, index) => (
                          <div
                            key={`dummy-${index}`}
                            className="shrink-0"
                            style={{
                              width: cardWidth > 0 ? `${cardWidth}px` : "auto",
                            }}
                          >
                            <ProductCardNew item={product} isLoading={true} />
                          </div>
                        ))
                      : bestSellerProducts?.map((product) => (
                          <div
                            key={product.product_id}
                            className="shrink-0"
                            style={{
                              width: cardWidth > 0 ? `${cardWidth}px` : "auto",
                              transition: "transform 0.2s",
                            }}
                          >
                            <ProductCardNew
                              item={product}
                              isLoading={false}
                              mutate={() => mutateBestSeller(bestSellerBody)}
                            />
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 60 }}>
              <div className="flex items-center gap-4 mb-4">
                <h2 className="text-2xl font-semibold shine-text">
                  Produk Lainnya
                </h2>
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background: "linear-gradient(90deg, #03AED2, #facc15)",
                  }}
                />
              </div>

              {isListLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div
                        style={{
                          background: "#e2e8f0",
                          borderRadius: 16,
                          height: 260,
                          marginBottom: 10,
                        }}
                      />
                      <div
                        style={{
                          background: "#e2e8f0",
                          borderRadius: 6,
                          height: 14,
                          marginBottom: 6,
                        }}
                      />
                      <div
                        style={{
                          background: "#e2e8f0",
                          borderRadius: 6,
                          height: 14,
                          width: "60%",
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : listProducts?.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <img
                    src={NotFound}
                    alt="Tidak ada produk"
                    style={{ width: 180, margin: "0 auto 16px" }}
                  />
                  <p style={{ color: "#94a3b8", fontSize: 15 }}>
                    Produk tidak ditemukan
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {listProducts?.map((product) => (
                    <div
                      key={product.product_id}
                      style={{ transition: "transform 0.2s" }}
                    >
                      <ProductCardNew
                        item={product}
                        isLoading={false}
                        mutate={() => mutateListProduct(reqBody)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomBarMobile />

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </>
  );
};

export default PurchaseList;
